import {
  HttpClient, HttpEvent, HttpHeaders,
} from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import {
  BehaviorSubject, Observable, Subject,
} from 'rxjs';

import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class EntityService {
  private _entityData$ = new BehaviorSubject<any>(null);
  public readonly entityData$ = this._entityData$.asObservable();

  private _reloadLogo$ = new Subject<void>();
  public readonly reloadLogo$ = this._reloadLogo$.asObservable();

  user: any;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toast: ToastService,
  ) {
    this.authService.user$.subscribe((user: any) => this.user = user)
  }

  triggerLogoReload() {
    this._reloadLogo$.next();
  }

  /**
   * Enriches entity data with a resolved AccountOwner object (id, fullName, email)
   * derived from Config.accountOwnerId (or legacy field names) and entity.Users.
   * "Enrich" here means: attach the resolved owner so callers don't need to repeat this logic.
   */
  private enrichEntityWithAccountOwner(entityData: any): any {
    if (!entityData) return entityData;

    const hasCurrent = typeof entityData === 'object' && entityData !== null && 'current' in entityData;
    const entity = hasCurrent ? (entityData.current ?? {}) : entityData;

    const ownerId =
      entity?.Config?.accountOwnerId ??
      entity?.Config?.account_owner_id ??
      entity?.Config?.supportUserId ??
      entity?.Config?.support_user_id ??
      null;

    if (ownerId == null) return entityData;

    const users = Array.isArray(entity?.Users) ? entity.Users : [];
    const ownerUser = users.find((u: any) => String(u?.id) === String(ownerId)) ?? null;

    const normalizedOwner = ownerUser ? {
      id: ownerUser.id,
      fullName: ownerUser.fullName ?? ownerUser.name ?? null,
      email: ownerUser.contactEmail ?? ownerUser.loginName ?? ownerUser.email ?? null,
    } : null;

    const enrichedEntity = {
      ...entity,
      accountOwnerId: ownerId,
      AccountOwner: normalizedOwner,
    };

    return hasCurrent ? { ...entityData, current: enrichedEntity } : enrichedEntity;
  }

  /** Returns the resolved account owner for the given entity (or current entity data). Reusable from any component. */
  getAccountOwner(entityData?: any): any {
    const currentEntityData = entityData ?? this.getEntityDataValue();
    const hydrated = this.enrichEntityWithAccountOwner(currentEntityData);
    const entity = hydrated?.current || hydrated;
    return entity?.AccountOwner ?? null;
  }

  getEntity = (includedAssociations: any) => {
    return this.http.get(`${environment.expressUrl}/entities`,
      {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params: { 'loginName': this.user.loginName, includedAssociations: includedAssociations },
      })
      .subscribe({
        next: (response: any) => {
          this._entityData$.next(this.enrichEntityWithAccountOwner(response.data));
          const entity = response?.data?.current ?? response?.data;
          const entityTimezone = entity?.Config?.localTimezone ?? entity?.localTimezone ?? null;
          console.log('[Entity] Logged-in entity timezone', {
            entityId: entity?.id,
            entityName: entity?.entityName ?? entity?.name,
            localTimezone: entityTimezone,
            note: entityTimezone == null ? 'No district timezone set; fallback (e.g. browser) will be used for course dates.' : undefined,
          });
        },
        error: (error) => {
          console.log('error', error);
        },
      })
  }

  getEntityLogo(loginName: string): Observable<any> {
    return this.http.get(`${environment.expressUrl}/entities/logo`,
      {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        params: { loginName },
      });
  }

  updateEntity = (entityData: any) => {

    const body = {
      loginName: this.user.loginName,
      ...entityData,
    }

    return this.http.put(
      `${environment.expressUrl}/entities`,
      body,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
      .subscribe({
        next: (response: any) => {
          // Some update responses don't return full nested objects (like Config).
          // Preserve what we already have in-memory and deep-merge Config so localTimezone persists.
          const prev = this.getEntityDataValue();
          const prevEntity = prev?.current || prev || {};
          const nextEntity = response?.data?.current || response?.data || {};

          const merged = {
            ...prevEntity,
            ...nextEntity,
            Config: {
              ...(prevEntity?.Config ?? {}),
              ...(nextEntity?.Config ?? {}),
              // normalize timezone field regardless of backend shape
              localTimezone:
                (nextEntity?.Config?.localTimezone ?? nextEntity?.localTimezone) ??
                (prevEntity?.Config?.localTimezone ?? prevEntity?.localTimezone) ??
                null,
            },
          };

          this._entityData$.next(this.enrichEntityWithAccountOwner(merged));

          this.toast.setToast({
            text: 'Entity updated',
            type: 'success',
            icon: true,
            dismissible: true,
          })
        },
        error: (error) => {
          console.log('error', error);
          this.toast.setToast({
            text: 'Error updating entity',
            type: 'error',
            icon: true,
            dismissible: true,
          })
        },
      })
  }

  generateSignedUrl = (
    fileName: string, mimeType: string, fileType: string, courseItemId?: number,
  ) => {
    return this.http.post(
      `${environment.expressUrl}/utils/get-signed-url`,
      {
        loginName: this.user.loginName,
        fileName,
        mimeType,
        fileType,
        courseItemId,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  }

  uploadSignedUrl(url: string, blobData: Blob): Observable<HttpEvent<any>> {
    return this.http.put(
      url, blobData, {
        reportProgress: true,
        observe: 'events',
        headers: new HttpHeaders({ 'Content-Type': blobData.type }),
        responseType: 'text',
      },
    );
  }

  getImageFromSignedUrl(signedUrl: string): Observable<Blob> {
    return this.http.get(signedUrl, { responseType: 'blob' });
  }

  setEntityLogo(
    loginName: string, filePath: string, fileName: string,
  ): Observable<any> {
    const body = {
      loginName, filePath, fileName,
    };
    return this.http.post(
      `${environment.expressUrl}/entities/logo`,
      body,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  }

  getEntityDataValue(): any {
    return this._entityData$.value;
  }

  getLocalTimezone(): string | null {
    const entityData = this.getEntityDataValue();
    const entity = entityData?.current || entityData;
    return entity?.Config?.localTimezone ?? entity?.localTimezone ?? null;
  }

  /**
   * District (entity) timezone with fallback so date/time conversion always has a timezone.
   * Use when building course create/update payloads so local time is never stored as UTC.
   * Prefer setting district timezone in system settings for correct multi-user behavior.
   */
  getLocalTimezoneWithFallback(): string {
    const district = this.getLocalTimezone();
    if (district) return district;
    if (isDevMode()) {
      console.warn(
        '[Entity] No district timezone set (Config.localTimezone). Course dates/times will use browser timezone as fallback. Set entity timezone in system settings for correct behavior.',
      );
    }
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  removeEntityLogo(loginName: string): Observable<any> {
    const body = { loginName };

    return this.http.delete(`${environment.expressUrl}/entities/logo`,
      {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        body,
      });
  }

}
